import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import FormRadioButton from '../../base/form/FormRadioButton';
import CreditCardInput from 'react-credit-card-input';
import { autorun } from 'mobx';
import Localized from '../../base/i18n/Localized';
import moment from 'moment-timezone';
import ReactLoading from 'react-loading';
import Modal from 'react-modal';
import { initiate3DSDataColletion } from '../../../utils/Utils';

moment.tz.setDefault('Asia/Dubai');

const Renew = inject('rootStore')(observer(
	class Renew extends Component {

		constructor(props) {
			super(props);

			this.renewRef = React.createRef();

			this.handleRenewClick = this.handleRenewClick.bind(this);
			
			this.scrollToTop = this.scrollToTop.bind(this);

			this.mobxAutorunDisposer = autorun(() => {
				const { pageTitlePrefix, translateMessage } = props.rootStore.commonStore;

				document.title = (pageTitlePrefix ? pageTitlePrefix + ' - ' : '') + translateMessage('page.renew.title');
			});

			this.state = {				
				// 3DS
				challengeOpen: false,
				stepUpUrl: null,
				accessToken: null
			};

			// Used as device fingerprinting reference
			this.referenceId = null;
			this.authenticationTransactionId = null;

			// Data collection (these need to be reset after each attempt!)
			this.dataCollectionTimeout = null;
			this.dataCollectionCompleted = false;
			this.enrollmentCheckInitiated = false;
		}

		componentDidMount() {
			const { userStore } = this.props.rootStore;

			userStore.resetRegistrationFields();

			userStore.loadCountries().then(result => {
				if (!result) {
					userStore.setError('Unable to get list of countries/nationalities, please reload the page and try again.');
				}
			});

			userStore.loadSubscriptions().then(result => {
				if (!result) {
					userStore.setError('Unable to get list of subscriptions, please reload the page and try again.');
				} else {
					// Set default subscription
					userStore.setSubscription(String(userStore.subscriptions.find(s => s.subscriptionMonths === 12).id));
				}
			});

			userStore.loadLevels().then(result => {
				if (!result) {
					userStore.setError('Unable to get list of levels, please reload the page and try again.');
				}
			});

			userStore.getLastTransactionDetails().then(details => {
				if (details) {
					userStore.setFirstName(details.billingFirstName);
					userStore.setLastName(details.billingLastName);
					userStore.setAddress(details.billingAddress);
					userStore.setCity(details.billingCity);
					userStore.setCountry(details.billingCountry);
					userStore.setPostalCode(details.billingPostalCode);
				}
			});

			// 3DS stuff
			// Setup listener for when data collection is complete
			window.addEventListener('message', this.handleWindowMessage, false);
		}

		componentWillUnmount() {
			this.mobxAutorunDisposer();

			window.removeEventListener('message', this.handleWindowMessage);
		}

		handleWindowMessage = event => {
			const { commonStore, userStore } = this.props.rootStore;
			const { translateMessage } = commonStore;

			if (event.origin === 'https://centinelapistag.cardinalcommerce.com' ||
					event.origin === 'https://centinelapi.cardinalcommerce.com')
			{
				console.debug('Data collection event message: ', event.data);

				const messageData = JSON.parse(event.data);
				
				if (messageData.MessageType === 'profile.completed' &&
						messageData.Status === true)
				{
					console.debug('Attempting to call enrollment check due to data collection message being received.')

					this.dataCollectionCompleted = true;

					clearTimeout(this.dataCollectionTimeout);

					this.doEnrollmentCheck(messageData);
				} else {
					userStore.setError(translateMessage('generic.error.threeDSDataCollectionError'));
				}
			}
		};

		doEnrollmentCheck = data => {	
			if (this.enrollmentCheckInitiated) {
				return;
			}

			this.enrollmentCheckInitiated = true;

			// Process response and initiate payment response via new V2 payment API
			// Based on response the payment will either go thru as before or there will be a need
			// to challenge the user by showing a popup with an iframe we post data too
			// Iframe load event will need to be monitored and intercepted for the challenge result,
			// after which we hide the iframe and finish the payment with the auth transaction ID we receive

			const { userStore } = this.props.rootStore;

			userStore.setThreeDSSetupInProgress(false);

			userStore.renew(this.referenceId).then(response => {
				if (response.status === "CHALLENGE_REQUIRED") {
					this.authenticationTransactionId = response.data.authenticationTransactionId;

					this.setState({
						challengeOpen: true,
						stepUpUrl: response.data.stepUpUrl,
						accessToken: response.data.accessToken
					});
				}
			});
		};

		handleSubscriptionChange = e => this.props.rootStore.userStore.setSubscription(e.target.value);
		handleFirstNameChange = e => {
			if (new RegExp('^[a-zA-Z\\-\\s]*$').test(e.target.value)) {
				this.props.rootStore.userStore.setFirstName(e.target.value);
			}
		}
		handleLastNameChange = e => {
			if (new RegExp('^[a-zA-Z\\-\\s]*$').test(e.target.value)) {
				this.props.rootStore.userStore.setLastName(e.target.value);
			}
		}
		handleAddressChange = e => {
			if (new RegExp('^[a-zA-Z\\d\\-\\s/\\.]*$').test(e.target.value)) {
				this.props.rootStore.userStore.setAddress(e.target.value);
			}
		}
		handlePostalCodeChange = e => {
			if (new RegExp('^[a-zA-Z\\d\\-\\s]*$').test(e.target.value)) {
				this.props.rootStore.userStore.setPostalCode(e.target.value);
			}
		}
		handleCityChange = e => {
			if (new RegExp('^[a-zA-Z\\-\\s]*$').test(e.target.value)) {
				this.props.rootStore.userStore.setCity(e.target.value);
			}
		}
		handleCountryChange = e => this.props.rootStore.userStore.setCountry(e.target.value);
		handleCcNumberChange = e => this.props.rootStore.userStore.setCcNumber(e.target.value);
		handleCcExpiryChange = e => this.props.rootStore.userStore.setCcExpiry(e.target.value);
		handleCcCvvChange = e => this.props.rootStore.userStore.setCcCvv(e.target.value);

		handleRenewClick(e) {
			e.preventDefault();

			const { commonStore, userStore } = this.props.rootStore;
			const { translateMessage } = commonStore;

			userStore.clearErrors();

			this.dataCollectionCompleted = false;
			this.enrollmentCheckInitiated = false;

			//userStore.upgrade();
			userStore.renewSetup().then(response => {
				if (response && response.deviceDataCollectionUrl && response.accessToken) {
					// Create iframe, post to iframe and wait for event
					this.referenceId = response.referenceId;

					initiate3DSDataColletion(response.deviceDataCollectionUrl, response.accessToken);

					// Call after 10 seconds
					this.dataCollectionTimeout = setTimeout(() => {
						console.debug('Calling enrollment check due to timeout on data collection.');

						this.doEnrollmentCheck();
					}, 10000);
				} else {
					userStore.setThreeDSSetupInProgress(false);
				}
			});

			this.scrollToTop();
		}

		scrollToTop() {
			if (this.renewRef.current) {
				requestAnimationFrame(() => {
					this.renewRef.current.scrollIntoView({
						behavior: 'smooth',
						block: 'start'
					});
				});
			}
		}

		redirectHandler = event => {
			try {
				if (event.target.contentWindow.location.href.includes('account/renew')) {
					this.setState({
						challengeOpen: false
					});

					// Try to complete payment
					const { userStore } = this.props.rootStore;
					
					userStore.renewComplete(this.authenticationTransactionId);
				}
			} catch (e) {

			}
		}

		render() {
			const { locale, translateMessage } = this.props.rootStore.commonStore;
			const { me, countries, subscriptions, levels, subscription, firstName, lastName, address, postalCode, city, country, ccNumber, ccExpiry, ccCvv, error, success, loading, threeDSSetupInProgress } = this.props.rootStore.userStore;
			//const freeRenew = me.companyCode ? !!me.companyCode.companyMembershipFree : false;
			const freeRenew = false;
			//const renewDisabled = !subscription || (!freeRenew && !(firstName && lastName && address && postalCode && city && country && ccNumber && ccExpiry && ccCvv));
			const renewDisabled = !subscription || !(firstName && lastName && address && postalCode && city && country && ccNumber && ccExpiry && ccCvv);
			const subscriptionInfo = subscriptions.find(s => String(s.id) === subscription);
			const currentLevel = levels.find(l => l.id === me.membershipLevel.id);
			const noRenew = me.membershipLevel.id === 4 || moment(me.subscriptionExpireDate).isAfter() || !!!me.companyCode.companyAllowRenew;

			return (
				<section ref={this.renewRef} className="section section--gray">
					<h2 className="section__title section__title--center">
						<Localized code="page.renew.title" />
					</h2>

					<div className="section__wrapper">
						<div className="renew">
							<div className="renew__form">
								<form className="form">
									{threeDSSetupInProgress && (
										<div className="form__message form__message--progress">
											<ReactLoading className="form__message-loading" type="spokes" color="#002652" height="36px" width="36px" />
											<Localized code="generic.text.threeDSInProgress" />
										</div>
									)}

									{!!error ? (
									<div className="form__message form__message--error">{error}</div>
									) : null}

									{!!success ? (
									<div className="form__section">
										<h4 className="form__title">
											<Localized code="page.renew.subtitle.success" />
										</h4>

										<p className="form__text">
											<Localized code="page.renew.text.success" />
										</p>

										<p className="form__text">
											<Link to="/account" className="button button--gold">
												<Localized code="page.renew.link.account" />
											</Link>
										</p>
									</div>
									) : null}

									{!!noRenew ? (
										<div className="form__section">
											<h4 className="form__title">
												<Localized code="page.renew.subtitle.notNeeded" />
											</h4>

											<p className="form__text">
												<Localized code="page.renew.text.notNeeded" />
											</p>

											<p className="form__text">
												<Link to="/account" className="button button--gold">
													<Localized code="page.renew.link.account" />
												</Link>
											</p>
										</div>
									) : null}

									{!success && !noRenew ? (
									<div className="form__section">
										<p className="form__text form__text--center">
											<Localized code="page.renew.text.begin" />
										</p>

										{currentLevel && subscriptionInfo ? (
											<div className="renew__level">
												<div className="renew__level-name">
													<div className="renew__level-label">
														<Localized code="page.renew.text.membershipLevel" />
													</div>
													<div className="renew__level-value">
														{me.companyCode.companyInitiative ?
															(locale === 'ar' ? me.companyCode.companyArShortName : me.companyCode.companyEnShortName) :
															currentLevel.levelName}
													</div>
												</div>

												<div className="renew__level-price">
													<div className="renew__level-label">
														<Localized code="page.renew.text.monthlyPrice" />
													</div>
													<div className="renew__level-value">{freeRenew ? 0 : currentLevel.levelAmount} AED</div>
												</div>

												<div className="renew__total-price">
													<div className="renew__level-label">
														<Localized code="page.renew.text.totalPrice" />
													</div>
													<div className="renew__level-value">{(freeRenew ? 0 : currentLevel.levelAmount) * subscriptionInfo.subscriptionMonths} AED</div>
												</div>
											</div>
										) : null}

										<h4 className="form__title">
											<Localized code="page.signup.subtitle.subscription" />
										</h4>

										<div className="form__group">
											<label className="form__label">
												<Localized code="page.signup.label.subscriptionPeriod" />
											</label>
											<div className="form__radios form__radios--inline">
												{subscriptions.slice().sort((a, b) => a.subscriptionMonths > b.subscriptionMonths ? -1 : 1).filter(s => 
													currentLevel && 
													(
														//((currentLevel.id === 1 || currentLevel.id === 2) && s.subscriptionMonths === 12) || 
														((currentLevel.id === 1 || currentLevel.id === 2) && (s.subscriptionMonths === 3 || s.subscriptionMonths === 6 || s.subscriptionMonths === 12)) || 
														((currentLevel.id !== 1 && currentLevel.id !== 2) && (s.subscriptionMonths === 3 || s.subscriptionMonths === 6 || s.subscriptionMonths === 12))
													)).map(s => (
													<FormRadioButton key={s.id} name="subscription" value={s.id} label={locale === 'ar' ? s.subscriptionNameAr : s.subscriptionNameEn} current={subscription} onChange={this.handleSubscriptionChange} />	
												))}
											</div>
										</div>

										{!freeRenew ? (
											<Fragment>
												<h4 className="form__title">
													<Localized code="page.signup.subtitle.billingDetails" />
												</h4>

												<div className="form__row">
													<div className="form__column">
														<div className="form__group">
															<label className="form__label" htmlFor="address">
																<Localized code="page.cart.label.address" />
															</label>
															<input className="form__input" type="text" id="address" value={address} onChange={this.handleAddressChange} />
														</div>
													</div>

													{false ? (
														<div className="form__column form__column--quarter">
															<div className="form__group">
																<label className="form__label" htmlFor="postalCode">
																	<Localized code="page.cart.label.postalCode" />
																</label>
																<input className="form__input" type="text" id="postalCode" value={postalCode} onChange={this.handlePostalCodeChange} />
															</div>
														</div>
													) : null}
												</div>

												<div className="form__row">
													<div className="form__column">
														<div className="form__group">
															<label className="form__label" htmlFor="country">
																<Localized code="page.cart.label.country" />
															</label>
															<div className="form__select-wrap">
																<select className="form__select" id="country" value={country} onChange={this.handleCountryChange}>
																	<option value="">{translateMessage('generic.label.pleaseSelect')}</option>
																	{countries.map(country => (
																		<option key={country.countryCode2c} value={country.countryCode2c}>{locale === 'ar' ? country.countryNameAr : country.countryName}</option>
																	))}
																</select>
															</div>
														</div>
													</div>

													<div className="form__column">
														<div className="form__group">
															<label className="form__label" htmlFor="city">
																<Localized code="page.cart.label.city" />
															</label>
															<input className="form__input" type="text" id="city" value={city} onChange={this.handleCityChange} />
														</div>
													</div>
												</div>

												<h4 className="form__title">
													<Localized code="page.signup.subtitle.paymentDetails" />
												</h4>

												<div className="form__row">
													<div className="form__column form__column--quarter">
														<div className="form__group">
															<label className="form__label" htmlFor="firstName">
																<Localized code="page.cart.label.firstName" />
															</label>
															<input className="form__input" type="text" id="firstName" value={firstName} onChange={this.handleFirstNameChange} />
														</div>
													</div>

													<div className="form__column form__column--quarter">
														<div className="form__group">
															<label className="form__label" htmlFor="lastName">
																<Localized code="page.cart.label.lastName" />
															</label>
															<input className="form__input" type="text" id="lastName" value={lastName} onChange={this.handleLastNameChange} />
														</div>
													</div>

													<div className="form__column">
														<div className="form__group">
															<label className="form__label">
																<Localized code="page.signup.label.ccDetails" />
															</label>
															<CreditCardInput
																containerClassName="form__cc-container"
																fieldClassName="form__cc-field"
																inputClassName="form__cc-input"
																dangerTextClassName="form__field-error"
																cardNumberInputProps={{ value: ccNumber, onChange: this.handleCcNumberChange }}
																cardExpiryInputProps={{ value: ccExpiry, onChange: this.handleCcExpiryChange }}
																cardCVCInputProps={{ value: ccCvv, onChange: this.handleCcCvvChange }}
															/>
														</div>
													</div>
												</div>
											</Fragment>
										) : null}

										<div className="form__buttons">
											<button disabled={renewDisabled || loading || threeDSSetupInProgress} className="form__button form__button--gold form__button--has-loading" type="submit" onClick={this.handleRenewClick}>
												{loading ? (
													<ReactLoading className="form__button-loading" type="spin" color="#FFF" height="20px" width="20px" />
												) : null}
												<Localized code="page.renew.button.renew" />
											</button>
										</div>
									</div>
									) : null}
								</form>
							</div>
						</div>
					</div>

					<Modal
						isOpen={this.state.challengeOpen}
						overlayClassName="modal"
						className="modal__content modal__content--challenge"
						ariaHideApp={false}
						bodyOpenClassName="locked"
						onAfterOpen={() => {
							// Setup event listeners
							document.getElementById('step-up-iframe').addEventListener('load', this.redirectHandler);

							document.getElementById('step-up-form').submit()
						}}
					>
						<iframe id="step-up-iframe" name="step-up-iframe" height="100%" width="100%"></iframe>
						<form id="step-up-form" target="step-up-iframe" method="post" action={this.state.stepUpUrl}>
							<input type="hidden" name="JWT" value={this.state.accessToken} />
							<input type="hidden" name="MD" value="fazaa" />
						</form>
					</Modal>
				</section>
			);
		}

	}
));

export default Renew;
